import { useState, useRef, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { ANNUAL_DISCOUNT, PLANS } from "@/config/plans";
import type { Plan } from "@/pages/Pricing/types";

/** PayPlus charges in ILS, so the pricing page advertises ILS regardless of UI language. */
const ILS_SYMBOL = "₪";

/** Starter is the value baseline every paid plan is measured against. */
const STARTER_PLAN = PLANS.find((p) => p.id === "starter");
const STARTER_PRICE_PER_CREDIT = STARTER_PLAN
  ? (STARTER_PLAN.monthlyPriceNIS ?? STARTER_PLAN.monthlyPrice) / STARTER_PLAN.tokens
  : 0;

export function usePricingCards() {
  const { profile } = useAuth();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("annual");
  const { t, language } = useLanguage();

  // Sync billing toggle to user's current billing period on initial load
  const initializedRef = useRef(false);
  useEffect(() => {
    if (profile?.billing_period && !initializedRef.current) {
      setBillingCycle(profile.billing_period as "monthly" | "annual");
      initializedRef.current = true;
    }
  }, [profile?.billing_period]);

  const getDisplayPrice = (plan: Plan) => {
    if (plan.monthlyPrice === 0) return 0;
    const baseNIS = plan.monthlyPriceNIS ?? plan.monthlyPrice;
    return billingCycle === "annual"
      ? Math.ceil(baseNIS * ANNUAL_DISCOUNT)
      : baseNIS;
  };

  const getAnnualTotalDisplay = (monthlyPrice: number, plan?: Plan) => {
    const baseNIS = plan?.monthlyPriceNIS ?? monthlyPrice;
    return Math.ceil(baseNIS * 12 * ANNUAL_DISCOUNT);
  };

  /** Undiscounted monthly price — shown struck-through next to the annual price. */
  const getMonthlyOriginalPrice = (plan: Plan) =>
    plan.monthlyPrice === 0 ? 0 : plan.monthlyPriceNIS ?? plan.monthlyPrice;

  /** Percentage saved each month by choosing annual (only meaningful in annual mode). */
  const getMonthlySavingsPercent = (plan: Plan) => {
    const original = getMonthlyOriginalPrice(plan);
    if (original === 0) return 0;
    return Math.round(((original - getDisplayPrice(plan)) / original) * 100);
  };

  /**
   * How much cheaper each credit is versus the Starter plan, as a percentage.
   * Returns 0 for Starter (the baseline) and Free (no price) so no badge shows.
   */
  const getValueSavingsVsStarterPercent = (plan: Plan) => {
    if (
      plan.monthlyPrice === 0 ||
      plan.id === "starter" ||
      STARTER_PRICE_PER_CREDIT === 0
    ) {
      return 0;
    }
    const planPerCredit =
      (plan.monthlyPriceNIS ?? plan.monthlyPrice) / plan.tokens;
    return Math.max(
      0,
      Math.round((1 - planPerCredit / STARTER_PRICE_PER_CREDIT) * 100),
    );
  };

  const toggleBillingCycle = () => {
    setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly");
  };

  return {
    billingCycle,
    toggleBillingCycle,
    t,
    language,
    currencySymbol: ILS_SYMBOL,
    getDisplayPrice,
    getAnnualTotalDisplay,
    getMonthlyOriginalPrice,
    getMonthlySavingsPercent,
    getValueSavingsVsStarterPercent,
  };
}
